import { formatAssignmentLine } from './flowAssignmentRevealHelper';

const getFlowFlatTranslation = (i18n, flatKey = '') => {
  if (!i18n || !flatKey) {
    return '';
  }
  const bundle = i18n.getResource(i18n.language, 'translation', 'flow');
  if (bundle && typeof bundle === 'object' && !Array.isArray(bundle) && bundle[flatKey]) {
    return bundle[flatKey];
  }
  return '';
};

export const resolveRuleQuestionKey = (rule = {}) => {
  if (rule.key_question) {
    return rule.key_question;
  }
  if (rule.key_description) {
    return rule.key_description.replace(/^rule\./, 'question.');
  }
  return '';
};

export const resolveRuleLabel = (rule = {}, t, i18n = null) => {
  const flatKey = rule.key_description || '';
  const fromFlat = getFlowFlatTranslation(i18n, flatKey);
  if (fromFlat) {
    return fromFlat;
  }
  if (flatKey) {
    const translated = t(`flow.${flatKey}`, { defaultValue: '' });
    if (translated && translated !== `flow.${flatKey}`) {
      return translated;
    }
  }
  return rule.description || rule.value || t('general.flow-engine.actions.generic', 'Acción');
};

export const resolveRuleQuestion = (rule = {}, t, i18n = null) => {
  const questionKey = resolveRuleQuestionKey(rule);
  const fromFlat = getFlowFlatTranslation(i18n, questionKey);
  if (fromFlat) {
    return fromFlat;
  }
  if (questionKey) {
    const translated = t(`flow.${questionKey}`, { defaultValue: '' });
    if (translated && translated !== `flow.${questionKey}`) {
      return translated;
    }
  }
  return rule.question || '';
};

const translateFlowKey = (key, t, i18n, fallback = '') => {
  if (!key) {
    return fallback;
  }
  const fromFlat = getFlowFlatTranslation(i18n, key);
  if (fromFlat) {
    return fromFlat;
  }
  const translated = t(`flow.${key}`, { defaultValue: fallback });
  if (translated && translated !== `flow.${key}`) {
    return translated;
  }
  return fallback;
};

const CLAIM_RULE_VALUES = new Set([
  'claim_from_pool_pre',
  'claim_from_feeder_pool',
  'claim_from_pre_pool',
  'claim_from_pool_manager',
  'claim_from_pool_verificator',
  'claim_from_pool_editor'
]);

const CLAIM_TRAY_LABELS = {
  manager: ['flow.claim.tray.manager', 'Reclamar de la Bandeja de Gestor'],
  validator: ['flow.claim.tray.validator', 'Reclamar de la Bandeja del Verificador'],
  verificator: ['flow.claim.tray.validator', 'Reclamar de la Bandeja del Verificador'],
  editor: ['flow.claim.tray.editor', 'Reclamar de la Bandeja del Editor'],
  'pre-manager': ['flow.claim.tray.pre-manager', 'Reclamar de la Bandeja del Pre-Gestor']
};

const normalizeClaimTrayRole = (roleKey = '') => {
  const key = String(roleKey || '').trim().toLowerCase();
  if (key === 'verificator' || key === 'verificador' || key === 'validador') {
    return 'validator';
  }
  return key;
};

export const resolveAssignmentButtonLabel = (rule = {}, t, i18n = null, options = {}) => {
  const raw = String(rule.value || '').trim().toLowerCase();
  if (raw === 'claim_from_pool_pre') {
    return t('flow.claim.tray.pre-return', 'Reclamar de la Bandeja del Pre-Gestor');
  }
  if (raw === 'claim_from_feeder_pool') {
    return t('flow.claim.tray.feeder', 'Reclamar de la Bandeja del Alimentador');
  }
  const trayKey = normalizeClaimTrayRole(options.roleKey);
  if (CLAIM_RULE_VALUES.has(raw) && CLAIM_TRAY_LABELS[trayKey]) {
    const [i18nKey, fallback] = CLAIM_TRAY_LABELS[trayKey];
    return t(i18nKey, fallback);
  }
  const presentation = rule.assignment_presentation;
  const buttonKey = presentation?.keys?.button;
  const fromPolicy = translateFlowKey(buttonKey, t, i18n, '');
  if (fromPolicy) {
    return fromPolicy;
  }
  return resolveRuleLabel(rule, t, i18n);
};

export const resolveAssignmentQuestion = (rule = {}, t, i18n = null) => {
  const presentation = rule.assignment_presentation;
  const questionKey = presentation?.keys?.question;
  const fromPolicy = translateFlowKey(questionKey, t, i18n, '');
  if (fromPolicy) {
    return fromPolicy;
  }
  return resolveRuleQuestion(rule, t, i18n);
};

export const resolveAssignmentDestination = (rule = {}, t, i18n = null, options = {}) => {
  const presentation = rule.assignment_presentation;
  if (!presentation) {
    return '';
  }
  if (options.reveal === false) {
    return '';
  }
  const record = presentation.preview_record || options.record || {};
  const id = record.id || options.record?.id || '—';
  const rawCode = record.code || options.record?.code || '';
  const code = String(rawCode || '').trim();
  const hasCode = Boolean(code) && code !== '—';
  const subjectKind = record.subject_kind || options.record?.subject_kind || '';
  if (subjectKind === 'search_key_term') {
    const name = presentation.preview_user?.name
      || t('flow.assignment.person-unknown', 'una persona');
    return formatAssignmentLine({
      id,
      code,
      name,
      usePool: presentation.delivery === 'pool',
      t,
      subjectKind,
      areaKey: record.area_key || ''
    });
  }
  if (presentation.delivery === 'pool') {
    if (!hasCode) {
      return t(
        'flow.assignment.destination.pool.detailNoCode',
        'El caso id:{{id}}, se asignará al mostrador (pool).',
        { id }
      );
    }
    return t(
      'flow.assignment.destination.pool.detail',
      'El caso id:{{id}}, codigo:{{code}}, se asignará al mostrador (pool).',
      { id, code }
    );
  }
  const name = presentation.preview_user?.name
    || t('flow.assignment.person-unknown', 'una persona');
  if (!hasCode) {
    return t(
      'flow.assignment.destination.person.detailNoCode',
      'El caso id:{{id}}, se asignará a : {{name}}',
      { id, name }
    );
  }
  return t(
    'flow.assignment.destination.person.detail',
    'El caso id:{{id}}, codigo:{{code}}, se asignará a : {{name}}',
    { id, code, name }
  );
};
